import { useState, useRef, useEffect, useCallback } from 'react';
import { CardPack3D } from './CardPack3D';
import { CardSummary, CardGrade } from '../../types/cards';

interface CardPackInteractionProps {
  cards: CardSummary[];
  onRevealComplete: () => void;
  onPackOpened?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onCardStateChange?: (cardStates: Map<number, CardRevealState>) => void;
  onCurrentRevealingIndexChange?: (index: number) => void;
}

// 카드 등장 애니메이션 단계
export type CardRevealPhase = 'hidden' | 'light' | 'appearing' | 'moving' | 'placed';

export interface CardRevealState {
  phase: CardRevealPhase;
  progress: number; // 0-1
  startTime: number;
}

// 등급별 등장 효과 설정
export const GRADE_REVEAL_CONFIG: Record<CardGrade, {
  lightDuration: number;
  appearDuration: number;
  moveDuration: number;
  scale: number;
}> = {
  SS: { lightDuration: 400, appearDuration: 1000, moveDuration: 500, scale: 1.2 },
  S: { lightDuration: 350, appearDuration: 900, moveDuration: 450, scale: 1.1 },
  A: { lightDuration: 300, appearDuration: 800, moveDuration: 400, scale: 1.05 },
  B: { lightDuration: 250, appearDuration: 700, moveDuration: 350, scale: 1.0 },
  C: { lightDuration: 200, appearDuration: 600, moveDuration: 300, scale: 0.95 },
  D: { lightDuration: 150, appearDuration: 500, moveDuration: 250, scale: 0.9 },
};

export function CardPackInteraction({ 
  cards, 
  onRevealComplete, 
  onPackOpened,
  onDragStart,
  onDragEnd,
  onCardStateChange,
  onCurrentRevealingIndexChange
}: CardPackInteractionProps) {
  const [openProgress, setOpenProgress] = useState(0);
  const [isOpening, setIsOpening] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [packAppearProgress, setPackAppearProgress] = useState(0);
  const [currentRevealingIndex, setCurrentRevealingIndex] = useState(-1);
  const [cardRevealStates, setCardRevealStates] = useState<Map<number, CardRevealState>>(new Map());
  const revealIntervalRef = useRef<number | null>(null);
  
  // 콜백을 ref로 저장하여 항상 최신 참조 유지 (실무 권장 방식)
  const onCardStateChangeRef = useRef(onCardStateChange);
  const onCurrentRevealingIndexChangeRef = useRef(onCurrentRevealingIndexChange);
  
  // 콜백이 변경될 때마다 ref 업데이트
  useEffect(() => {
    onCardStateChangeRef.current = onCardStateChange;
  }, [onCardStateChange]);
  
  useEffect(() => {
    onCurrentRevealingIndexChangeRef.current = onCurrentRevealingIndexChange;
  }, [onCurrentRevealingIndexChange]);

  // 모래 폭풍 등장 애니메이션
  useEffect(() => {
    const timer = setInterval(() => {
      setPackAppearProgress((prev) => {
        if (prev >= 1) {
          clearInterval(timer);
          return 1;
        }
        return prev + 0.08;
      });
    }, 30);
    
    return () => clearInterval(timer);
  }, []);

  // 자동으로 모래 폭풍이 열리는 애니메이션
  useEffect(() => {
    if (packAppearProgress >= 1 && !isOpening && !isOpened) {
      const delay = setTimeout(() => {
        setIsOpening(true);
        onDragStart?.();
      }, 1000);
      
      return () => clearTimeout(delay);
    }
  }, [packAppearProgress, isOpening, isOpened, onDragStart]);

  // 카드 등장 시퀀스 시작 (useCallback으로 메모이제이션)
  const startCardRevealSequence = useCallback(() => {
    if (cards.length === 0) {
      setTimeout(() => onRevealComplete(), 1000);
      return;
    }

    let cardIndex = 0;

    const revealNextCard = () => {
      if (cardIndex >= cards.length) {
        // 모든 카드 등장 완료
        setTimeout(() => {
          onRevealComplete();
        }, 1000);
        return;
      }

      const card = cards[cardIndex];
      const currentCardIndex = cardIndex; // 클로저에 캡처
      setCurrentRevealingIndex(currentCardIndex);
      onCurrentRevealingIndexChangeRef.current?.(currentCardIndex);
      
      // 카드 등장 시작
      const startTime = Date.now();
      setCardRevealStates((prev) => {
        const newMap = new Map(prev);
        const updatedState = {
          phase: 'light' as CardRevealPhase,
          progress: 0,
          startTime
        };
        newMap.set(currentCardIndex, updatedState); // 인덱스를 키로 사용
        // ref를 통해 최신 콜백 호출 (즉시 실행)
        onCardStateChangeRef.current?.(newMap);
        return newMap;
      });

      // 카드 등장 애니메이션 진행
      const config = GRADE_REVEAL_CONFIG[card.grade];
      const totalDuration = config.lightDuration + config.appearDuration + config.moveDuration;
      
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        
        setCardRevealStates((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(currentCardIndex); // 캡처된 인덱스로 조회
          if (!state) return newMap;

          let phase: CardRevealPhase = state.phase;
          const lightEnd = config.lightDuration / totalDuration;
          const appearEnd = (config.lightDuration + config.appearDuration) / totalDuration;

          if (progress < lightEnd) {
            phase = 'light';
          } else if (progress < appearEnd) {
            phase = 'appearing';
          } else if (progress < 1) {
            phase = 'moving';
          } else {
            phase = 'placed';
          }

          const updatedState = {
            phase,
            progress,
            startTime: state.startTime
          };
          newMap.set(currentCardIndex, updatedState); // 캡처된 인덱스를 키로 사용
          
          // ref를 통해 최신 콜백 호출 (즉시 실행, 60fps에 최적화)
          onCardStateChangeRef.current?.(newMap);
          
          return newMap;
        });

        if (progress >= 1) {
          clearInterval(interval);
          // 다음 카드로
          cardIndex++;
          setTimeout(revealNextCard, 200); // 카드 간 간격
        }
      }, 16); // ~60fps
    };

    revealNextCard();
  }, [cards, onRevealComplete, onCardStateChangeRef, onCurrentRevealingIndexChangeRef]);

  // 자동으로 openProgress 증가
  useEffect(() => {
    if (isOpening && !isOpened) {
      const timer = setInterval(() => {
        setOpenProgress((prev) => {
          const newProgress = prev + 0.02;
          
          if (newProgress >= 1) {
            clearInterval(timer);
            setIsOpened(true);
            setOpenProgress(1);
            onPackOpened?.();
            startCardRevealSequence();
            return 1;
          }
          
          return newProgress;
        });
      }, 30);
      
      return () => clearInterval(timer);
    }
  }, [isOpening, isOpened, onPackOpened, startCardRevealSequence]);

  return (
    <>
      {/* 모래 폭풍만 렌더링 */}
      <CardPack3D
        cards={cards}
        isOpening={isOpening}
        openProgress={openProgress}
        onOpenComplete={() => {}}
        position={[
          0, 
          packAppearProgress < 1 ? -1.5 + packAppearProgress * 1.5 : 0,
          0
        ]}
      />
    </>
  );
}

// 카드 위치 계산 함수 (2D 렌더링용)
export function getCardPositionFor2D(card: CardSummary, index: number, total: number, state: CardRevealState | undefined): { x: number; y: number; scale: number; rotation: number; opacity: number } {
  if (!state || state.phase === 'hidden') {
    return { x: 0, y: 0, scale: 0, rotation: 0, opacity: 0 };
  }

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 375;
  const cardWidth = 180; // 카드 기본 최대 너비 (px)
  const horizontalPadding = 48; // 좌우 여백 확보
  const computedRadius = (viewportWidth - cardWidth - horizontalPadding) / 2;
  const safeRadius = Math.max(60, Math.min(150, computedRadius));

  const config = GRADE_REVEAL_CONFIG[card.grade];
  const lightEnd = config.lightDuration / (config.lightDuration + config.appearDuration + config.moveDuration);
  const appearEnd = (config.lightDuration + config.appearDuration) / (config.lightDuration + config.appearDuration + config.moveDuration);

  // Phase 1-2: 화면 중앙에서 등장
  if (state.phase === 'light' || state.phase === 'appearing') {
    const appearProgress = state.progress < lightEnd ? 0 : (state.progress - lightEnd) / (appearEnd - lightEnd);
    const easedScale = 1 - Math.pow(1 - appearProgress, 3);
    // light 단계에서도 약간 보이도록 (최소 0.2 opacity)
    const minOpacity = state.phase === 'light' ? 0.2 : 0;
    return {
      x: 0,
      y: 0,
      scale: easedScale,
      rotation: appearProgress * 360,
      opacity: Math.max(minOpacity, appearProgress)
    };
  }

  // Phase 3: 최종 위치로 이동
  if (state.phase === 'moving') {
    const moveProgress = (state.progress - appearEnd) / (1 - appearEnd);
    const easedMove = 1 - Math.pow(1 - moveProgress, 3);
    
    // 최종 위치 계산 (원형 배치)
    const finalAngle = (index / total) * Math.PI * 2;
    const finalX = Math.cos(finalAngle) * safeRadius * easedMove;
    const finalY = Math.sin(finalAngle) * safeRadius * easedMove;

    return {
      x: finalX,
      y: finalY,
      scale: config.scale,
      rotation: 0,
      opacity: 1
    };
  }

  // Phase 4: 최종 위치
  const finalAngle = (index / total) * Math.PI * 2;
  return {
    x: Math.cos(finalAngle) * safeRadius,
    y: Math.sin(finalAngle) * safeRadius,
    scale: config.scale,
    rotation: 0,
    opacity: 1
  };
}

